import { FC, lazy, PropsWithChildren, Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import AuthProvider, { RothoSSOSettings, useAuthContext } from "../context/authContext";
import { BackdropProvider } from "../context/modalContext";
import NotificationDispatch, { useNotificationContext } from "../context/notificationContext";
import { GoogleMapsInitializer } from "../hooks/useGoogleMaps";
import { useIubenda } from "../hooks/useIubenda";
import useToggle from "../hooks/useToggle";
import useI18n from "../hooks/useTranslations";
import styles from "./App.module.css";
import Configuration from "./configurations/Configuration";
import Configurations from "./configurations/Configurations";
import ManageConfigurationInvite from "./configurations/ManageConfigurationInvite";
import SharedConfigurations from "./configurations/SharedConfigurations";
import HeaderBar from "./HeaderBar";
import Home from "./home/Home";
import Item from "./items/Item";
import Items from "./items/Items";
import MainMenu from "./MainMenu";
import Documents from "./projects/Documents";
import ManageProjectInvite from "./projects/ManageProjectInvite";
import Project from "./projects/Project";
import Projects from "./projects/Projects";
import SharedProjects from "./projects/SharedProjects";
import SideBar from "./SideBar";
import System from "./systems/System";
import Error from "./ui/Error";
import ErrorText from "./ui/ErrorText";
import FlexContent from "./ui/FlexContent";
import Header from "./ui/Header";
import LoadingSpinner from "./ui/LoadingSpinner";
import ModalDialog from "./ui/ModalDialog";
import Notification from "./ui/Notification";

// Admin-only screens: lazy-loaded so non-admin users never download this code,
// and it lands in its own chunk(s) instead of the main app bundle.
const AdminClients = lazy(() => import("./clients/AdminClients"));
const EditAdminClient = lazy(() => import("./clients/EditAdminClient").then((m) => ({ default: m.EditAdminClient })));
const NewAdminClient = lazy(() => import("./clients/NewAdminClient").then((m) => ({ default: m.NewAdminClient })));
const AdminConfigurations = lazy(() => import("./configurations/AdminConfigurations"));
const NewAdminItem = lazy(() => import("./items/AdminItem").then((m) => ({ default: m.NewAdminItem })));
const EditAdminItem = lazy(() => import("./items/AdminItem").then((m) => ({ default: m.EditAdminItem })));
const AdminItems = lazy(() => import("./items/AdminItems"));
const AdminDocuments = lazy(() => import("./projects/AdminDocuments"));
const AdminProjects = lazy(() => import("./projects/AdminProjects"));
const EditAdminSystem = lazy(() => import("./systems/AdminSystem").then((m) => ({ default: m.EditAdminSystem })));
const AdminSystems = lazy(() => import("./systems/AdminSystems"));
const AdminSystemSettings = lazy(() => import("./systems/adminSystemSettings/AdminSystemSettings"));
const EditBallastType = lazy(() => import("./systems/adminSystemSettings/border/ballastTypes/EditBallastType"));
const GuardrailComposition = lazy(
  () => import("./systems/adminSystemSettings/border/guardrailCompositions/GuardrailComposition"),
);
const Normative = lazy(() => import("./systems/adminSystemSettings/border/normatives/Normative"));
const EditSubstructure = lazy(() => import("./systems/adminSystemSettings/border/substructures/EditSubstructure"));
const BorderSystem = lazy(() => import("./systems/adminSystemSettings/border/systems/System"));
const UprightType = lazy(() => import("./systems/adminSystemSettings/border/uprightTypes/UprightType"));
const FasteningOptions = lazy(() => import("./systems/adminSystemSettings/hrail/fasteningOptions/FasteningOptions"));
const HrailSystem = lazy(() => import("./systems/adminSystemSettings/hrail/systems/System"));
const Membrane = lazy(() => import("./systems/adminSystemSettings/patrol/membranes/Membrane"));
const NewMembrane = lazy(() => import("./systems/adminSystemSettings/patrol/membranes/NewMembrane"));
const PatrolNormative = lazy(() => import("./systems/adminSystemSettings/patrol/normatives/Normative"));
const RoofCategory = lazy(() => import("./systems/adminSystemSettings/patrol/roofCategories/RoofCategory"));
const RoofType = lazy(() => import("./systems/adminSystemSettings/patrol/roofCategories/roofTypes/RoofType"));
const RoofMaterial = lazy(() => import("./systems/adminSystemSettings/patrol/roofMaterials/RoofMaterial"));
const RoofSubstructure = lazy(() => import("./systems/adminSystemSettings/patrol/roofSubstructures/RoofSubstructure"));
const PatrolSteelVersion = lazy(() => import("./systems/adminSystemSettings/patrol/steelVersions/PatrolSteelVersion"));
const PatrolSystem = lazy(() => import("./systems/adminSystemSettings/patrol/systems/System"));
const PatrolSystemType = lazy(() => import("./systems/adminSystemSettings/patrol/systemTypes/SystemType"));
const PatrolText = lazy(() => import("./systems/adminSystemSettings/patrol/texts/Text"));
const InstallationType = lazy(
  () => import("./systems/adminSystemSettings/vertigrip/installationTypes/InstallationType"),
);
const NewFixing = lazy(() =>
  import("./systems/adminSystemSettings/vertigrip/installationTypes/wallMaterials/fixings/Fixing").then((m) => ({
    default: m.NewFixing,
  })),
);
const EditFixing = lazy(() =>
  import("./systems/adminSystemSettings/vertigrip/installationTypes/wallMaterials/fixings/Fixing").then((m) => ({
    default: m.EditFixing,
  })),
);
const WallMaterial = lazy(
  () => import("./systems/adminSystemSettings/vertigrip/installationTypes/wallMaterials/WallMaterial"),
);
const VertigripNormative = lazy(() => import("./systems/adminSystemSettings/vertigrip/normatives/Normative"));
const VertigripSteelVersion = lazy(
  () => import("./systems/adminSystemSettings/vertigrip/steelVersions/VertigripSteelVersion"),
);
const VertiripSystemType = lazy(() => import("./systems/adminSystemSettings/vertigrip/systemTypes/SystemType"));
const VertigripText = lazy(() => import("./systems/adminSystemSettings/vertigrip/texts/Text"));
const AdminGlobalSettings = lazy(() => import("./systems/globalSettings/AdminGlobalSettings"));
const GlobalSteelVersion = lazy(() => import("./systems/globalSettings/steelVersions/GlobalSteelVersion"));
const AdminUser = lazy(() => import("./users/AdminUser").then((m) => ({ default: m.AdminUser })));
const AdminUsers = lazy(() => import("./users/AdminUsers"));

const App: FC = () => {
  const settings: RothoSSOSettings = {
    clientId: import.meta.env.VITE_CLIENT_ID,
    iamUrl: import.meta.env.VITE_IAM_URL,
  };

  const [{ show, severity, message, sticky }, dispatch] = useNotificationContext();

  return (
    <BrowserRouter>
      <BackdropProvider>
        <AuthProvider settings={settings}>
          <NotificationDispatch.Provider value={dispatch}>
            <Routes>
              {/* Public routes - no authentication needed */}
              <Route path="/invite/project/:token" element={<Invite settings={settings} type="project" />} />
              <Route
                path="/invite/configuration/:token"
                element={<Invite settings={settings} type="configuration" />}
              />

              {/* All other routes require authentication */}
              <Route path="/*" element={<AuthenticatedLayout />} />
            </Routes>

            {show && <Notification type={severity} text={message ?? ""} sticky={sticky} />}
          </NotificationDispatch.Provider>
        </AuthProvider>
      </BackdropProvider>
    </BrowserRouter>
  );
};

export default App;

const AuthenticatedLayout: FC = () => {
  const { isExpired, logout, isLoading, permissions, init } = useAuthContext();
  const i18n = useI18n();
  const sidebar = useToggle();
  useIubenda();

  useEffect(() => {
    init(true);
    if (import.meta.env.MODE === "staging" && !document.title.includes(i18n.translation.common.testEnvironment)) {
      document.title += " - " + i18n.translation.common.testEnvironment;
    }
  }, []);

  return isLoading ? (
    <div className={styles.loadingSpinner}>
      <LoadingSpinner />
    </div>
  ) : (
    <>
      <GoogleMapsInitializer />
      <HeaderBar sidebar={sidebar} />
      <AppContent>
        <SideBar isOpen={sidebar.isOpen} close={sidebar.close}>
          <MainMenu onClose={sidebar.close} />
        </SideBar>
        {!isExpired && (
          <AppPageContent>
            <Suspense
              fallback={
                <div className={styles.loadingSpinner}>
                  <LoadingSpinner />
                </div>
              }
            >
              <Routes>
                <Route index element={<Home />} />
                <Route path="items" element={<Items />} />
                <Route path="items/:itemId" element={<Item />} />
                <Route path="systems/:systemId" element={<System />} />

                <Route path="projects" element={<Projects />} />
                <Route path="projects/shared" element={<SharedProjects />} />
                <Route path="projects/:projectId" element={<Project />} />

                <Route path="configurations" element={<Configurations />} />
                <Route path="configurations/shared" element={<SharedConfigurations />} />
                <Route path="configurations/:configurationId" element={<Configuration />} />
                <Route path="projects/:projectId/configurations/:configurationId" element={<Configuration />} />

                <Route path="documents" element={<Documents />} />

                {!!permissions.admin.canManageProjects && (
                  <>
                    <Route path="admin/projects" element={<AdminProjects />} />
                    <Route path="admin/projects/:projectId" element={<Project isAdmin />} />
                    <Route path="admin/documents" element={<AdminDocuments />} />
                    <Route path="admin/configurations" element={<AdminConfigurations />} />
                    <Route path="admin/configurations/:configurationId" element={<Configuration isAdmin />} />
                    <Route
                      path="admin/projects/:projectId/configurations/:configurationId"
                      element={<Configuration isAdmin />}
                    />
                  </>
                )}

                {!!permissions.admin.canManageSystems && (
                  <>
                    <Route path="admin/settings" element={<AdminGlobalSettings />} />
                    <Route path="admin/settings/steel_versions/:steelVersionId" element={<GlobalSteelVersion />} />
                    <Route path="admin/systems" element={<AdminSystems />} />
                    <Route path="admin/systems/:systemId" element={<EditAdminSystem />} />
                    <Route path="admin/systems/:systemId/settings" element={<AdminSystemSettings />} />
                    <Route
                      path="admin/systems/:systemId/vertigrip_normatives/:normativeId"
                      element={<VertigripNormative />}
                    />
                    <Route
                      path="admin/systems/:systemId/patrol_steel_versions/:steelVersionId"
                      element={<PatrolSteelVersion />}
                    />
                    <Route
                      path="admin/systems/:systemId/vertigrip_steel_versions/:steelVersionId"
                      element={<VertigripSteelVersion />}
                    />
                    <Route
                      path="admin/systems/:systemId/vertigrip_system_types/:systemTypeId"
                      element={<VertiripSystemType />}
                    />
                    <Route path="admin/systems/:systemId/vertigrip_static_texts/:textId" element={<VertigripText />} />
                    <Route
                      path="admin/systems/:systemId/installation_types/:installationTypeId"
                      element={<InstallationType />}
                    />
                    <Route
                      path="admin/systems/:systemId/installation_types/:installationTypeId/wall_materials/:wallMaterialId"
                      element={<WallMaterial />}
                    />
                    <Route
                      path="admin/systems/:systemId/installation_types/:installationTypeId/wall_materials/:wallMaterialId/fixings/new"
                      element={<NewFixing />}
                    />
                    <Route
                      path="admin/systems/:systemId/installation_types/:installationTypeId/wall_materials/:wallMaterialId/fixings/:fixingId"
                      element={<EditFixing />}
                    />
                    <Route path="admin/systems/:systemId/roof_categories/:categoryId" element={<RoofCategory />} />
                    <Route
                      path="admin/systems/:systemId/roof_categories/:categoryId/roof_types/:roofTypeId"
                      element={<RoofType />}
                    />
                    <Route
                      path="admin/systems/:systemId/roof_substructures/:roofSubstructureId"
                      element={<RoofSubstructure />}
                    />
                    <Route path="admin/systems/:systemId/roof_materials/:roofMaterialId" element={<RoofMaterial />} />
                    {/* <Route
                      path="admin/systems/:systemId/support_materials/:supportMaterialId"
                      element={<SteelVersion />}
                    /> */}
                    <Route path="admin/systems/:systemId/patrol_systems/:patrolSystemId" element={<PatrolSystem />} />
                    <Route
                      path="admin/systems/:systemId/patrol_normatives/:normativeId"
                      element={<PatrolNormative />}
                    />
                    <Route
                      path="admin/systems/:systemId/patrol_system_types/:patrolSystemTypeId"
                      element={<PatrolSystemType />}
                    />
                    {/* <Route path="admin/systems/:systemId/patrol_elements/new" element={<NewElement />} />
                    <Route path="admin/systems/:systemId/patrol_elements/:elementId" element={<EditElement />} /> */}
                    <Route path="admin/systems/:systemId/border_systems/:borderSystemId" element={<BorderSystem />} />
                    <Route
                      path="admin/systems/:systemId/guardrail_compositions/:guardrailCompositionId"
                      element={<GuardrailComposition />}
                    />
                    <Route path="admin/systems/:systemId/normatives/:normativeId" element={<Normative />} />
                    <Route path="admin/systems/:systemId/ballast_types/:ballastTypeId" element={<EditBallastType />} />
                    <Route
                      path="admin/systems/:systemId/substructures/:substructureId"
                      element={<EditSubstructure />}
                    />
                    <Route path="admin/systems/:systemId/upright_types/:uprightTypeId" element={<UprightType />} />
                    <Route path="admin/systems/:systemId/membranes/new" element={<NewMembrane />} />
                    <Route path="admin/systems/:systemId/membranes/:membraneId" element={<Membrane />} />
                    <Route path="admin/systems/:systemId/patrol_static_texts/:textId" element={<PatrolText />} />

                    <Route path="admin/systems/:systemId/hrail_systems/:entityId" element={<HrailSystem />} />
                    <Route
                      path="admin/systems/:systemId/hrail/fastening_options/:entityId"
                      element={<FasteningOptions />}
                    />
                  </>
                )}

                {!!permissions.admin.canManageUsers && (
                  <>
                    <Route path="admin/users" element={<AdminUsers />} />
                    <Route path="admin/users/:userId" element={<AdminUser />} />
                  </>
                )}

                {!!permissions.admin.canManageItems && (
                  <>
                    <Route path="admin/items" element={<AdminItems />} />
                    <Route path="admin/items/new" element={<NewAdminItem />} />
                    <Route path="admin/items/:itemId" element={<EditAdminItem />} />
                  </>
                )}

                {!!permissions.admin.canManageClients && (
                  <>
                    <Route path="admin/clients" element={<AdminClients />} />
                    <Route path="admin/clients/new" element={<NewAdminClient />} />
                    <Route path="admin/clients/:clientId" element={<EditAdminClient />} />
                  </>
                )}

                <Route path="error" element={<Error />} />
                <Route
                  path="*"
                  element={
                    <>
                      <Header title={i18n.translation.common.notFound} />
                      <FlexContent padding>
                        <ErrorText text={i18n.translation.common.resourceNotFound} />
                      </FlexContent>
                    </>
                  }
                />
              </Routes>
            </Suspense>
          </AppPageContent>
        )}
      </AppContent>
      {!!isExpired && (
        <ModalDialog
          onClose={() => logout()}
          title={i18n.translation.common.sessionExpired}
          actions={[
            {
              title: i18n.translation.common.close,
              onClick: () => logout(),
            },
          ]}
          isSubmitting={false}
        >
          {i18n.translation.common.sessionExpired}
        </ModalDialog>
      )}
    </>
  );
};

type InviteProps = {
  settings: RothoSSOSettings;
  type: "project" | "configuration";
};

const Invite: FC<InviteProps> = ({ settings, type }: InviteProps) => {
  const { init, identity, permissions } = useAuthContext();
  const sidebar = useToggle();
  useIubenda();

  useEffect(() => {
    init(false);
  }, []);

  const hasAdminPermissions = !!(
    permissions.admin.canManageProjects ||
    permissions.admin.canManageSystems ||
    permissions.admin.canManageUsers ||
    permissions.admin.canManageItems ||
    permissions.admin.canManageClients
  );

  return (
    <>
      <HeaderBar sidebar={identity && hasAdminPermissions ? sidebar : undefined} />
      <AppContent>
        {(identity || hasAdminPermissions) && (
          <SideBar isOpen={sidebar.isOpen} close={sidebar.close}>
            <MainMenu onClose={sidebar.close} />
          </SideBar>
        )}
        <AppPageContent>
          {type === "project" ? (
            <ManageProjectInvite settings={settings} />
          ) : (
            <ManageConfigurationInvite settings={settings} />
          )}
        </AppPageContent>
      </AppContent>
    </>
  );
};

const AppContent: FC<PropsWithChildren> = ({ children }) => <div className={styles.content}> {children}</div>;

const AppPageContent: FC<PropsWithChildren> = ({ children }) => <div className={styles.pageContent}> {children}</div>;
